import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GetPaymentForms } from 'services/payments/getPaymentForms';
import { GetServiceTerms } from 'services/payments/getServiceTerms';
import { GetCustomerCharges } from 'services/payments/getCustomerCharges';
import { GetPaymentAvailable } from 'services/payments/getPaymentAvailable';
import { GetBillingsByCustomerId } from 'services/payments/getBillingsByCustomerId';

import { PostConfirmOrder } from 'services/payments/postConfirmOrder';
import { PostGerarCobranca } from 'services/payments/postGerarCobranca';
import { PostPaymentSlipApp } from 'services/payments/postPaymentSlipApp';
import { PostGenerateBilling } from 'services/payments/postGenerateBilling';
import { PostFinancialPending } from 'services/payments/postFinancialPending';
import { PostAcceptTerm } from 'services/payments/postAcceptTerm';
import { PostValidateSolicitationDate } from 'services/payments/postValidateSolicitationDate';
import { PostChangeBillingPaymentMethod } from 'services/payments/postChangeBillingPaymentMethod';
import { PostChangeBillingPaymentDueDate } from 'services/payments/postChangeBillingPaymentDueDate';
import { PostListarFormaPagamentoPorCanalVenda } from 'services/payments/postListarFormaPagamentoPorCanalVenda';

import { ParamsCustomerCharges, ParamsPaymentForms, ParamsBank, ParamsBodyPaymentSlipApp, ParamsBodyFinancialPending, ParamsBodyAcceptTerm, ParamsDataFinancialPending, ParamsGenerateBilling, ParamsDataGenerateBillingPix, ParamsDataGenerateBillingTicket, ParamsBodyChangeBillingPaymentMethod, ParamsBodyListarFormaPagamentoPorCanalVenda, ParamsDataListarFormaPagamentoPorCanalVenda, ParamsBodyGerarCobranca, ParamsDataGerarCobranca, ParamsBodyConfirmOrder, ParamsDataConfirmOrder, ParamsBodyChangeBillingPaymentDueDate, ParamsPaymentFormsInitial, ParamsValidateSolicitationDate, ParamsDataValidateSolicitationDate, ParamsDataValidateChoose } from 'utils/typeServices';

const defaultValueTicket = {
    CdCobranca: 0,
    Digitavel: '',
    EmailEnviado: false,
    EmpFil: 0,
    Html: '',
    Pdf: '',
    IsError: false
}

const defaultValuePix = {
    CdCobranca: 0,
    EmailEnviado: false,
    EmpFil: 0,
    PixQrCode: '',
    PixQrCodeBase64: '',
    IsError: false
}

const usePayments = () => {
    const navigate = useNavigate();
    const [isPendingTicket, setIsPendingTicket] = useState<boolean>();
    const [serviceTerms, setServiceTerms] = useState<HTMLElement>();
    const [formOfPayment, setFormOfPayment] = useState<Array<ParamsPaymentFormsInitial>>([]);
    const [customerPaymentForms, setCustomerPaymentForms] = useState<Array<ParamsPaymentForms>>();
    const [listPaymentAvailable, setListPaymentAvailable] = useState<Array<ParamsBank>>([]);
    const [invoicesCustomer, setInvoicesCustomer] = useState<Array<ParamsCustomerCharges>>([]);
    const [dataGerarCobranca, setDataGerarCobranca] = useState<ParamsDataGerarCobranca | false>();
    const [dataConfirmOrder, setDataConfirmOrder] = useState<ParamsDataConfirmOrder | false>();
    const [dataFinancialPending, setDataFinancialPending] = useState<Array<ParamsDataFinancialPending> | false | string>();
    const [dataChangeBillingPaymentMethod, setDataChangeBillingPaymentMethod] = useState<any | false>();
    const [dataChangeBillingPaymentDueDate, setDataChangeBillingPaymentDueDate] = useState<any | false>();
    const [dataGenerateBillingPix, setDataGenerateBillingPix] = useState<ParamsDataGenerateBillingPix>(defaultValuePix);
    const [dataValidateSolicitationDate, setDataValidateSolicitationDate] = useState<ParamsDataValidateSolicitationDate | false>();
    const [dataGenerateBillingTicket, setDataGenerateBillingTicket] = useState<ParamsDataGenerateBillingTicket>(defaultValueTicket);
    const [dataGetPaymentTicketScheduling, setDataGetPaymentTicketScheduling] = useState<ParamsDataGenerateBillingTicket | false>();
    const [dataListarFormaPagamentoPorCanalVenda, setDataListarFormaPagamentoPorCanalVenda] = useState<Array<ParamsDataListarFormaPagamentoPorCanalVenda>>();

    const handleClearPayments = () => {
        setServiceTerms(undefined);
        setCustomerPaymentForms(undefined);
        setListPaymentAvailable([]);
        setInvoicesCustomer([]);
        setFormOfPayment([]);
        setIsPendingTicket(undefined);
        setDataConfirmOrder(undefined);
        setDataGerarCobranca(undefined);
        setDataFinancialPending(undefined);
        setDataValidateSolicitationDate(undefined);
        setDataChangeBillingPaymentMethod(undefined);
        setDataChangeBillingPaymentDueDate(undefined);
        setDataGenerateBillingPix(defaultValuePix);
        setDataGenerateBillingTicket(defaultValueTicket);
        setDataListarFormaPagamentoPorCanalVenda(undefined);
    }

    const handleDataGerarCobranca = () => {
        setDataGerarCobranca(undefined);
    }

    // Função para atualizar o estado após aceitar o termo
    const handleAcceptTermo = () => {
        if (dataFinancialPending && typeof dataFinancialPending === 'object' && !Array.isArray(dataFinancialPending)) {
            const responseData = dataFinancialPending as any;
            if (responseData?.Data?.Cobrancas) {
                setDataFinancialPending(responseData.Data.Cobrancas);
            }
        }
    }

    const handleGetCustomerCharges = async ({ token, nrPlatform }: { token: string, nrPlatform: number }) => {
        const { data } = await GetCustomerCharges({ token, nrPlatform });

        if (data) {
            setInvoicesCustomer(data.Faturas);
            setFormOfPayment(data.FormaPagmento);
        }
    }

    const handleGetBillingsByCustomerId = async ({ token, pendingTicket = false }: { token: string, pendingTicket?: boolean }) => {
        const { data } = await GetBillingsByCustomerId({ token });

        if (data) {
            if (!pendingTicket) {
                setInvoicesCustomer(data);
            }
        }
    }

    const handleGetPaymentForms = async ({ token }: { token: string }) => {
        const { data } = await GetPaymentForms({ token });

        data && setCustomerPaymentForms(data);
    }

    const handleGetPaymentAvailable = async ({ token, type }: { token: string, type: number }) => {
        const { data } = await GetPaymentAvailable({ token, type });

        data && setListPaymentAvailable(data);
    }

    const handleGetServiceTerms = async ({ token, type }: { token: string, type: number }) => {
        const { data } = await GetServiceTerms({ token, type });

        data && setServiceTerms(data);
    }

    const handlePostPaymentSlipApp = async ({ token, body }: { token: string, body: ParamsBodyPaymentSlipApp }) => {
        PostPaymentSlipApp({ token, body });

        // data && setServiceTerms(data);
    }

    const handlePostFinancialPending = async ({ token, body }: { token: string | null, body: ParamsBodyFinancialPending }) => {
        setDataFinancialPending(undefined);
        const { data, error_msg } = await PostFinancialPending({ token, body });

        if (data?.Cobrancas && data?.Cobrancas.length >= 0) {
            // Type assertion para acessar TermoIturanHub
            const responseData = data as any;
            
            // Se existe termo na resposta, salvar o objeto completo, senão apenas as cobranças
            if (responseData?.TermoIturanHub?.TermoIturanHubBase64) {
                setDataFinancialPending({ Data: data } as any);
            } else {
                setDataFinancialPending(data.Cobrancas);
            }
            
            const dateNow = new Date(new Date().setHours(0, 0, 0, 0));

            if (data?.Cobrancas.find(({ DT_VENCIMENTO, NR_STATUS }: any) => (NR_STATUS === 1) && (new Date(`${DT_VENCIMENTO.split('/')[1]}/${DT_VENCIMENTO.split('/')[0]}/${DT_VENCIMENTO.split('/')[2]}`).getTime() < dateNow.getTime()))) {
                setIsPendingTicket(true);
            } else {
                setIsPendingTicket(false);
            }
        } else if (error_msg) {
            setDataFinancialPending(error_msg);
        } else {
            setDataFinancialPending(false);
        }
    }

    const handlePostGenerateBilling = async ({ token, body, type }: { token: string, body: ParamsGenerateBilling, type: 'ticket' | 'pix' | 'email' }) => {
        if (body.EMAIL === '') {
            setDataGenerateBillingPix(JSON.parse(JSON.stringify(defaultValuePix)));
            setDataGenerateBillingTicket(JSON.parse(JSON.stringify(defaultValueTicket)));
        }

        const { data } = await PostGenerateBilling({ token, CHAVE_PAGAMENTO: body.CHAVE_PAGAMENTO, ISPIX: type === 'pix' ? true : false, EMAIL: body.EMAIL, CD_FORMA_PAGAMENTO: body.CD_FORMA_PAGAMENTO });

        if (data) {
            if (type === 'pix') {
                body.EMAIL ?
                    setDataGenerateBillingPix(prevState => ({ ...prevState, EmailEnviado: data.EmailEnviado }))
                    :
                    setDataGenerateBillingPix(data);
            } else if (type === 'ticket') {
                body.EMAIL ?
                    setDataGenerateBillingTicket(prevState => ({ ...prevState, EmailEnviado: data.EmailEnviado }))
                    :
                    setDataGenerateBillingTicket(data);
            }
        } else {
            if (type === 'pix') {
                setDataGenerateBillingPix(prevState => ({
                    ...prevState,
                    EmailEnviado: false,
                    IsError: true
                }))
            } else {
                setDataGenerateBillingTicket(prevState => ({
                    ...prevState,
                    EmailEnviado: false,
                    IsError: true
                }))
            }
        }
    }

    const handlePostChangeBillingPaymentMethod = async ({ token, body }: { token: string, body: ParamsBodyChangeBillingPaymentMethod }) => {
        setDataChangeBillingPaymentMethod(undefined);
        const { data } = await PostChangeBillingPaymentMethod({ token, body });

        if (data) {
            setDataChangeBillingPaymentMethod(data);
            handleGetPaymentForms({ token: localStorage.getItem('token')! });
        } else if (data === false) {
            setDataChangeBillingPaymentMethod(false);
        } else {
            setDataChangeBillingPaymentMethod(null);
        }
    }

    const handlePostChangeBillingPaymentDueDate = async ({ body }: { body: ParamsBodyChangeBillingPaymentDueDate }) => {
        setDataChangeBillingPaymentDueDate(undefined);
        const { data } = await PostChangeBillingPaymentDueDate({ body });

        if (data) {
            setDataChangeBillingPaymentDueDate(data);
            handleGetPaymentForms({ token: localStorage.getItem('token')! });
        } else if (data === false) {
            setDataChangeBillingPaymentDueDate(false);
        } else {
            setDataChangeBillingPaymentDueDate(null);
        }
    }

    const handlePostListarFormaPagamentoPorCanalVenda = async ({ token, body }: { token: string, body: ParamsBodyListarFormaPagamentoPorCanalVenda }) => {
        const { data } = await PostListarFormaPagamentoPorCanalVenda({ token, body });

        if (data) {
            setDataListarFormaPagamentoPorCanalVenda(data);
        }
    }

    const handlePostGerarCobranca = async ({ token, body }: { token: string, body: ParamsBodyGerarCobranca }) => {
        setDataGerarCobranca(undefined);
        const { data } = await PostGerarCobranca({ token, body });

        if (data) {
            setDataGerarCobranca(data);
        } else {
            setDataGerarCobranca(false);
        }
    }

    const handlePostConfirmOrder = async ({ body, isWebView }: { body: ParamsBodyConfirmOrder, isWebView?: boolean }) => {
        setDataConfirmOrder(undefined);
        const data = await PostConfirmOrder({ body });

        if (data?.success) {           
            setDataConfirmOrder(data.data);
            if(body?.FL_AGENDAMENTO && !data?.data?.fL_ATENDIMENTO_INICIAL_MANUTENCAO){
                navigate(`${isWebView ? '/wb' : ''}/central-de-trocas/troca-de-servico/agendamento`);               
            }else{             
                navigate(`${isWebView ? '/wb' : ''}/central-de-trocas/troca-de-servico/resumo-pedido`);
            }         
        } else {
            setDataConfirmOrder(false);
        }
    }

    const handleGetPaymentTicketScheduling = async ({ CD_COBRANCA_TAXA_REAGENDAMENTO, body }: { CD_COBRANCA_TAXA_REAGENDAMENTO: number, body: ParamsBodyFinancialPending }) => {
        setDataGetPaymentTicketScheduling(undefined);
        const token = localStorage.getItem('token')!;
        const { data } = await PostFinancialPending({ token, body });

        if (data?.Cobrancas) {
            const ticketSelected = data?.Cobrancas?.find(({ CD_COBRANCA }: any) => CD_COBRANCA === CD_COBRANCA_TAXA_REAGENDAMENTO);

            if (ticketSelected) {
                const { data: ticketScheduling } = await PostGenerateBilling({ token: token, CHAVE_PAGAMENTO: ticketSelected?.CHAVE_PAGAMENTO, ISPIX: false });

                if (ticketScheduling) {
                    setDataGetPaymentTicketScheduling(ticketScheduling);
                } else {
                    setDataGetPaymentTicketScheduling(false);
                }
            }
        }
    }

    const handlePostValidateSolicitationDate = async ({ body }: { body: ParamsValidateSolicitationDate }) => {
        const data = await PostValidateSolicitationDate({ body });

        if (data) {
            setDataValidateSolicitationDate(data);
        } else {
            setDataValidateSolicitationDate(false);
        }
    }

    const handlePostAcceptTerm = async ({ token, body }: { token: string | null, body: ParamsBodyAcceptTerm }) => {
        const result = await PostAcceptTerm({ token, body });

        if (result.ok) {
            return result.data;
        }

        if (result.rejectLike && body.Accept) {
            const err = new Error(result.error_msg || 'Erro ao processar aceite do termo') as Error & { code?: string };
            err.code = 'REJECT_TERM_FLOW';
            throw err;
        }

        throw new Error(result.error_msg || 'Erro ao processar aceite do termo');
    }

    return { dataChangeBillingPaymentDueDate, dataConfirmOrder, invoicesCustomer, customerPaymentForms, listPaymentAvailable, serviceTerms, dataFinancialPending, dataGenerateBillingPix, dataGenerateBillingTicket, dataListarFormaPagamentoPorCanalVenda, dataGerarCobranca, dataChangeBillingPaymentMethod, formOfPayment, dataGetPaymentTicketScheduling, isPendingTicket, dataValidateSolicitationDate, handleGetCustomerCharges, handleGetPaymentForms, handleGetPaymentAvailable, handleGetServiceTerms, handlePostPaymentSlipApp, handlePostFinancialPending, handlePostGenerateBilling, handlePostChangeBillingPaymentMethod, handlePostListarFormaPagamentoPorCanalVenda, handlePostGerarCobranca, handleClearPayments, handlePostConfirmOrder, handlePostChangeBillingPaymentDueDate, handleGetBillingsByCustomerId, handleGetPaymentTicketScheduling, handleDataGerarCobranca, handlePostValidateSolicitationDate, handleAcceptTermo, handlePostAcceptTerm };
}

export default usePayments;